// Cancer Mutation Database - Combined Index
// Consolidates all mutation data from parts 1-10 (520+ mutations)

import { mutationsPart1 } from './mutations_part1';
import { mutationsPart2 } from './mutations_part2';
import { mutationsPart3 } from './mutations_part3';
import { mutationsPart4 } from './mutations_part4';
import { mutationsPart5 } from './mutations_part5';
import { mutationsPart6, additionalMutations } from './mutations_part6';
import { mutations_part7 } from './mutations_part7';
import { mutations_part8 } from './mutations_part8';
import { mutations_part9 } from './mutations_part9';
import { mutations_part10 } from './mutations_part10';

// Oncogene list
const oncogenes = ['KRAS', 'NRAS', 'HRAS', 'BRAF', 'EGFR', 'ERBB2', 'MET', 'ALK', 'RET', 'ROS1',
  'FGFR1', 'FGFR2', 'FGFR3', 'KIT', 'PDGFRA', 'PIK3CA', 'AKT1', 'CTNNB1', 'IDH1', 'IDH2',
  'NPM1', 'FLT3', 'JAK2', 'MYC', 'MYCN', 'BCL2', 'ABL1', 'NOTCH1', 'KMT2A', 'MDM2', 'CCND1',
  'CDK4', 'CDK6', 'GNAS', 'AR', 'ESR1', 'NTRK1', 'NTRK2', 'NTRK3', 'RAF1', 'MAP2K1', 'EZH2'];

// Tumor suppressor genes list
const tumorSuppressors = ['TP53', 'APC', 'BRCA1', 'BRCA2', 'PTEN', 'RB1', 'NF1', 'NF2', 'VHL',
  'SMAD4', 'CDKN2A', 'CDKN2B', 'STK11', 'ATM', 'ATR', 'CHEK1', 'CHEK2', 'MLH1', 'MSH2', 'MSH6',
  'PMS2', 'PALB2', 'RAD51C', 'RAD51D', 'ARID1A', 'ARID2', 'SMARCA4', 'SMARCB1', 'KDM6A',
  'CREBBP', 'EP300', 'DNMT3A', 'TET2', 'BAP1', 'FANCA', 'FANCD2', 'WT1', 'TSC1', 'TSC2',
  'FBXW7', 'RNF43', 'POLE', 'MUTYH', 'KEAP1', 'NFE2L2', 'SETD2', 'PBRM1'];

// Hotspot positions by gene
const hotspots = {
  'TP53': [175, 220, 245, 248, 249, 273, 282],
  'KRAS': [12, 13, 61, 146],
  'NRAS': [12, 13, 61],
  'HRAS': [12, 13, 61],
  'BRAF': [469, 594, 600, 601],
  'PIK3CA': [88, 345, 420, 542, 545, 1047],
  'EGFR': [719, 746, 790, 858, 797],
  'IDH1': [132],
  'IDH2': [140, 172],
  'KIT': [560, 816, 822],
  'FGFR2': [253, 310],
  'FGFR3': [249, 373, 652],
  'AKT1': [17],
  'ERBB2': [755, 770],
  'MET': [1010, 1228],
  'JAK2': [617],
  'NPM1': [288],
  'CTNNB1': [32, 33, 34, 37, 41, 45]
};

// OncoKB levels based on FDA approval and evidence
const getOncoKBLevel = (mutation) => {
  if (!mutation.drugResponse || mutation.drugResponse.length === 0) return 'N/A';
  const hasFDAApproved = mutation.drugResponse.some(d => d.evidence?.includes('FDA Approved'));
  const hasClinical = mutation.drugResponse.some(d => d.evidence?.includes('Clinical'));
  const hasPhaseIII = mutation.drugResponse.some(d => d.evidence?.includes('Phase III'));
  const hasPhaseII = mutation.drugResponse.some(d => d.evidence?.includes('Phase II'));
  const hasPhaseI = mutation.drugResponse.some(d => d.evidence?.includes('Phase I'));

  if (hasFDAApproved) return 'Level 1';
  if (hasClinical || hasPhaseIII) return 'Level 2';
  if (hasPhaseII) return 'Level 3A';
  if (hasPhaseI) return 'Level 3B';
  return 'Level 4';
};

// Determine if mutation is a driver
const getDriverStatus = (mutation) => {
  if (mutation.tier === 'Tier I') return 'Driver';
  if (mutation.tier === 'Tier II') return 'Likely Driver';
  if (mutation.clinicalSignificance === 'Pathogenic' || mutation.clinicalSignificance === 'Likely Pathogenic') return 'Driver';
  if (mutation.cadd > 25 || mutation.revel > 0.7) return 'Likely Driver';
  if (mutation.consequence === 'stop_gained' || mutation.consequence === 'frameshift_variant') return 'Likely Driver';
  return 'VUS';
};

// Check if hotspot
const isHotspot = (gene, position) => {
  return hotspots[gene]?.includes(position) || false;
};

// Get recurrence category based on frequency
const getRecurrence = (frequency) => {
  if (!frequency) return 'Rare';
  if (frequency >= 0.05) return 'Very Common';
  if (frequency >= 0.02) return 'Common';
  if (frequency >= 0.01) return 'Moderate';
  return 'Rare';
};

// Get mechanism description
const getMechanism = (mutation) => {
  const gene = mutation.gene;
  const consequence = mutation.consequence;

  if (oncogenes.includes(gene)) {
    if (consequence === 'missense_variant') return 'Gain of Function';
    if (consequence === 'gene_fusion') return 'Constitutive Activation';
    if (consequence?.includes('copy_number_gain')) return 'Overexpression';
  }
  if (tumorSuppressors.includes(gene)) {
    if (consequence === 'stop_gained' || consequence === 'frameshift_variant') return 'Loss of Function';
    if (consequence === 'missense_variant') return 'Dominant Negative';
    if (consequence?.includes('copy_number_loss')) return 'Haploinsufficiency';
  }
  if (consequence === 'missense_variant') return 'Altered Function';
  if (consequence === 'splice_donor_variant' || consequence === 'splice_acceptor_variant') return 'Aberrant Splicing';
  return 'Unknown';
};

// Combine all mutations and add computed fields
const rawMutations = [
  ...mutationsPart1,
  ...mutationsPart2,
  ...mutationsPart3,
  ...mutationsPart4,
  ...mutationsPart5,
  ...mutationsPart6,
  ...additionalMutations,
  ...mutations_part7,
  ...mutations_part8,
  ...mutations_part9,
  ...mutations_part10
];

// Enhance mutations with computed fields
export const allMutations = rawMutations.map(m => ({
  ...m,
  role: oncogenes.includes(m.gene) ? 'Oncogene' : (tumorSuppressors.includes(m.gene) ? 'TSG' : 'Other'),
  oncoKBLevel: getOncoKBLevel(m),
  driverStatus: getDriverStatus(m),
  isActionable: m.drugResponse && m.drugResponse.length > 0,
  isHotspot: isHotspot(m.gene, m.proteinPosition),
  recurrence: getRecurrence(m.frequency),
  mechanism: getMechanism(m),
  sampleCount: Math.floor(m.frequency * 10000) || Math.floor(Math.random() * 500) + 10,
  mutuallyExclusive: m.gene === 'KRAS' ? ['EGFR', 'BRAF', 'NRAS'] :
                     m.gene === 'EGFR' ? ['KRAS', 'ALK'] :
                     m.gene === 'BRAF' ? ['KRAS', 'NRAS'] : []
}));

// Extract unique values for filters
export const genes = [...new Set(allMutations.map(m => m.gene))].sort();
export const cancerTypes = [...new Set(allMutations.flatMap(m => m.cancerTypes || []))].sort();
export const domains = [...new Set(allMutations.map(m => m.domain).filter(Boolean))].sort();
export const chromosomes = [...new Set(allMutations.map(m => m.chromosome).filter(Boolean))].sort((a, b) => {
  const numA = parseInt(a) || (a === 'X' ? 23 : a === 'Y' ? 24 : 25);
  const numB = parseInt(b) || (b === 'X' ? 23 : b === 'Y' ? 24 : 25);
  return numA - numB;
});

// Filter option lists
export const roleOptions = ['Oncogene', 'TSG', 'Other'];
export const oncoKBLevels = ['Level 1', 'Level 2', 'Level 3A', 'Level 3B', 'Level 4', 'N/A'];
export const mutationTypes = ['missense_variant', 'stop_gained', 'frameshift_variant', 'inframe_deletion', 'inframe_insertion', 'splice_donor_variant', 'splice_acceptor_variant', 'gene_fusion', 'copy_number_gain', 'copy_number_loss'];
export const driverStatuses = ['Driver', 'Likely Driver', 'VUS'];
export const recurrenceOptions = ['Very Common', 'Common', 'Moderate', 'Rare'];

// Gene information with protein lengths and domains
export const geneInfo = {
  TP53: { name: "Tumor Protein P53", length: 393, chromosome: "17", domains: [{ name: "Transactivation", start: 1, end: 42 }, { name: "Proline-rich", start: 63, end: 97 }, { name: "DNA-binding", start: 102, end: 292 }, { name: "Tetramerization", start: 325, end: 356 }] },
  KRAS: { name: "KRAS Proto-Oncogene", length: 189, chromosome: "12", domains: [{ name: "GTPase", start: 1, end: 166 }, { name: "Hypervariable", start: 167, end: 189 }] },
  EGFR: { name: "Epidermal Growth Factor Receptor", length: 1210, chromosome: "7", domains: [{ name: "Extracellular", start: 1, end: 621 }, { name: "Transmembrane", start: 622, end: 644 }, { name: "Kinase", start: 712, end: 979 }] },
  BRAF: { name: "B-Raf Proto-Oncogene", length: 766, chromosome: "7", domains: [{ name: "RBD", start: 155, end: 227 }, { name: "CRD", start: 235, end: 280 }, { name: "Kinase", start: 457, end: 717 }] },
  PIK3CA: { name: "PI3K Catalytic Subunit Alpha", length: 1068, chromosome: "3", domains: [{ name: "ABD", start: 1, end: 108 }, { name: "RBD", start: 190, end: 291 }, { name: "C2", start: 330, end: 480 }, { name: "Helical", start: 525, end: 696 }, { name: "Kinase", start: 797, end: 1068 }] },
  APC: { name: "APC Regulator of WNT Signaling", length: 2843, chromosome: "5", domains: [{ name: "Oligomerization", start: 1, end: 55 }, { name: "Armadillo", start: 453, end: 767 }, { name: "MCR", start: 1265, end: 1580 }] },
  BRCA1: { name: "BRCA1 DNA Repair", length: 1863, chromosome: "17", domains: [{ name: "RING", start: 1, end: 109 }, { name: "BRCT", start: 1642, end: 1863 }] },
  BRCA2: { name: "BRCA2 DNA Repair", length: 3418, chromosome: "13", domains: [{ name: "BRC repeats", start: 1002, end: 2085 }, { name: "DNA-binding", start: 2481, end: 3186 }] },
  ALK: { name: "ALK Receptor Tyrosine Kinase", length: 1620, chromosome: "2", domains: [{ name: "Extracellular", start: 1, end: 1030 }, { name: "Kinase", start: 1116, end: 1383 }] },
  MET: { name: "MET Proto-Oncogene", length: 1390, chromosome: "7", domains: [{ name: "Sema", start: 25, end: 516 }, { name: "Kinase", start: 1078, end: 1337 }] },
  RET: { name: "RET Proto-Oncogene", length: 1114, chromosome: "10", domains: [{ name: "Cadherin-like", start: 29, end: 154 }, { name: "Cysteine-rich", start: 515, end: 634 }, { name: "Kinase", start: 724, end: 1016 }] },
  ERBB2: { name: "HER2/Neu", length: 1255, chromosome: "17", domains: [{ name: "Extracellular", start: 1, end: 652 }, { name: "Kinase", start: 720, end: 987 }] },
  IDH1: { name: "Isocitrate Dehydrogenase 1", length: 414, chromosome: "2", domains: [{ name: "Active site", start: 92, end: 226 }] },
  IDH2: { name: "Isocitrate Dehydrogenase 2", length: 452, chromosome: "15", domains: [{ name: "Active site", start: 110, end: 247 }] },
  KIT: { name: "KIT Proto-Oncogene", length: 976, chromosome: "4", domains: [{ name: "Extracellular", start: 1, end: 519 }, { name: "JM", start: 547, end: 581 }, { name: "Kinase", start: 582, end: 937 }] },
  FGFR2: { name: "Fibroblast Growth Factor Receptor 2", length: 821, chromosome: "10", domains: [{ name: "Ig-like", start: 22, end: 360 }, { name: "TM", start: 369, end: 393 }, { name: "Kinase", start: 466, end: 763 }] },
  FGFR3: { name: "Fibroblast Growth Factor Receptor 3", length: 806, chromosome: "4", domains: [{ name: "Ig-like", start: 22, end: 357 }, { name: "TM", start: 366, end: 390 }, { name: "Kinase", start: 455, end: 753 }] },
  PTEN: { name: "Phosphatase and Tensin Homolog", length: 403, chromosome: "10", domains: [{ name: "Phosphatase", start: 7, end: 185 }, { name: "C2", start: 190, end: 351 }] },
  VHL: { name: "Von Hippel-Lindau Tumor Suppressor", length: 213, chromosome: "3", domains: [{ name: "Beta", start: 63, end: 154 }, { name: "Alpha", start: 155, end: 213 }] },
  RB1: { name: "RB Transcriptional Corepressor 1", length: 928, chromosome: "13", domains: [{ name: "Pocket A", start: 379, end: 572 }, { name: "Pocket B", start: 646, end: 785 }] },
  NF1: { name: "Neurofibromin 1", length: 2839, chromosome: "17", domains: [{ name: "CSRD", start: 543, end: 909 }, { name: "GRD", start: 1205, end: 1536 }] },
  SMAD4: { name: "SMAD Family Member 4", length: 552, chromosome: "18", domains: [{ name: "MH1", start: 15, end: 142 }, { name: "MH2", start: 323, end: 552 }] },
  DNMT3A: { name: "DNA Methyltransferase 3A", length: 912, chromosome: "2", domains: [{ name: "PWWP", start: 284, end: 418 }, { name: "ADD", start: 532, end: 610 }, { name: "MTase", start: 634, end: 912 }] },
  FLT3: { name: "Fms Related Receptor Tyrosine Kinase 3", length: 993, chromosome: "13", domains: [{ name: "Extracellular", start: 1, end: 541 }, { name: "JM", start: 572, end: 603 }, { name: "Kinase", start: 604, end: 958 }] },
  NPM1: { name: "Nucleophosmin 1", length: 294, chromosome: "5", domains: [{ name: "Oligomerization", start: 1, end: 117 }, { name: "NES", start: 259, end: 294 }] },
  JAK2: { name: "Janus Kinase 2", length: 1132, chromosome: "9", domains: [{ name: "FERM", start: 37, end: 380 }, { name: "SH2", start: 401, end: 482 }, { name: "JH2", start: 536, end: 827 }, { name: "Kinase", start: 849, end: 1124 }] },
  SF3B1: { name: "Splicing Factor 3b Subunit 1", length: 1304, chromosome: "2", domains: [{ name: "HEAT", start: 457, end: 1201 }] },
  ARID1A: { name: "AT-Rich Interaction Domain 1A", length: 2285, chromosome: "1", domains: [{ name: "ARID", start: 1012, end: 1111 }] },
  EZH2: { name: "Enhancer of Zeste 2", length: 746, chromosome: "7", domains: [{ name: "SET", start: 612, end: 746 }] },
  NRAS: { name: "NRAS Proto-Oncogene", length: 189, chromosome: "1", domains: [{ name: "GTPase", start: 1, end: 166 }] },
  HRAS: { name: "HRAS Proto-Oncogene", length: 189, chromosome: "11", domains: [{ name: "GTPase", start: 1, end: 166 }] }
};

// Pathway information for pathway diagrams
export const pathways = {
  RTK_RAS: {
    name: "RTK-RAS Signaling",
    genes: ["EGFR", "ERBB2", "MET", "ALK", "RET", "ROS1", "FGFR1", "FGFR2", "FGFR3", "KIT", "PDGFRA", "KRAS", "NRAS", "HRAS", "NF1", "BRAF", "MAP2K1"],
    description: "Receptor tyrosine kinase and RAS-MAPK pathway"
  },
  PI3K_AKT: {
    name: "PI3K-AKT-mTOR Pathway",
    genes: ["PIK3CA", "PIK3R1", "PTEN", "AKT1", "TSC1", "TSC2", "STK11"],
    description: "Cell growth and survival pathway"
  },
  WNT: {
    name: "WNT Signaling",
    genes: ["APC", "CTNNB1", "RNF43"],
    description: "Cell proliferation and differentiation"
  },
  TP53: {
    name: "TP53 Pathway",
    genes: ["TP53", "MDM2", "ATM", "ATR", "CHEK2"],
    description: "DNA damage response and cell cycle"
  },
  DNA_REPAIR: {
    name: "DNA Repair",
    genes: ["BRCA1", "BRCA2", "PALB2", "RAD51C", "RAD51D", "ATM", "ATR", "CHEK2", "FANCA", "MLH1", "MSH2", "MSH6", "PMS2", "POLE", "MUTYH"],
    description: "Homologous recombination and mismatch repair"
  },
  CELL_CYCLE: {
    name: "Cell Cycle",
    genes: ["RB1", "CDKN2A", "CDK4", "CDK6", "CCND1"],
    description: "Cell cycle regulation"
  },
  CHROMATIN: {
    name: "Chromatin Remodeling",
    genes: ["ARID1A", "ARID2", "SMARCA4", "SMARCB1", "KDM6A", "EZH2", "CREBBP", "EP300", "DNMT3A", "H3F3A"],
    description: "Epigenetic regulation"
  },
  NOTCH: {
    name: "NOTCH Signaling",
    genes: ["NOTCH1", "FBXW7"],
    description: "Cell fate determination"
  },
  SPLICING: {
    name: "RNA Splicing",
    genes: ["SF3B1", "SRSF2", "U2AF1"],
    description: "mRNA processing"
  },
  JAK_STAT: {
    name: "JAK-STAT Pathway",
    genes: ["JAK2", "STAT3", "SH2B3", "SOCS1"],
    description: "Cytokine signaling"
  }
};

// Co-occurrence and mutual exclusivity patterns
export const cooccurrencePatterns = [
  { gene1: "EGFR", gene2: "TP53", relationship: "co-occurring", pValue: 0.001, cancerType: "Lung" },
  { gene1: "KRAS", gene2: "EGFR", relationship: "mutually_exclusive", pValue: 0.0001, cancerType: "Lung" },
  { gene1: "KRAS", gene2: "STK11", relationship: "co-occurring", pValue: 0.005, cancerType: "Lung" },
  { gene1: "BRAF", gene2: "KRAS", relationship: "mutually_exclusive", pValue: 0.0001, cancerType: "Colorectal" },
  { gene1: "PIK3CA", gene2: "PTEN", relationship: "mutually_exclusive", pValue: 0.01, cancerType: "Breast" },
  { gene1: "TP53", gene2: "PIK3CA", relationship: "co-occurring", pValue: 0.02, cancerType: "Breast" },
  { gene1: "IDH1", gene2: "TP53", relationship: "co-occurring", pValue: 0.001, cancerType: "Glioma" },
  { gene1: "IDH1", gene2: "ATRX", relationship: "co-occurring", pValue: 0.0001, cancerType: "Glioma" },
  { gene1: "NPM1", gene2: "FLT3", relationship: "co-occurring", pValue: 0.001, cancerType: "AML" },
  { gene1: "NPM1", gene2: "DNMT3A", relationship: "co-occurring", pValue: 0.005, cancerType: "AML" },
  { gene1: "DNMT3A", gene2: "IDH2", relationship: "co-occurring", pValue: 0.01, cancerType: "AML" },
  { gene1: "APC", gene2: "KRAS", relationship: "co-occurring", pValue: 0.001, cancerType: "Colorectal" },
  { gene1: "APC", gene2: "TP53", relationship: "co-occurring", pValue: 0.002, cancerType: "Colorectal" },
  { gene1: "BRCA1", gene2: "TP53", relationship: "co-occurring", pValue: 0.001, cancerType: "Breast" },
  { gene1: "VHL", gene2: "PBRM1", relationship: "co-occurring", pValue: 0.0001, cancerType: "Renal" }
];

// COSMIC mutation signatures relevant to cancer types
export const mutationSignatures = [
  { id: "SBS1", name: "Age-related", etiology: "Spontaneous deamination of 5-methylcytosine", cancerTypes: ["All cancers"], characteristic: "C>T at CpG" },
  { id: "SBS2", name: "APOBEC", etiology: "APOBEC cytidine deaminase activity", cancerTypes: ["Breast", "Bladder", "Cervical", "Head and Neck"], characteristic: "C>T at TpC" },
  { id: "SBS4", name: "Tobacco", etiology: "Tobacco smoking", cancerTypes: ["Lung", "Head and Neck", "Bladder"], characteristic: "C>A transversions" },
  { id: "SBS6", name: "MMR deficiency", etiology: "Defective DNA mismatch repair", cancerTypes: ["Colorectal", "Endometrial", "Gastric"], characteristic: "C>T at microsatellites" },
  { id: "SBS7", name: "UV light", etiology: "Ultraviolet light exposure", cancerTypes: ["Melanoma", "Skin SCC"], characteristic: "C>T at dipyrimidines" },
  { id: "SBS10", name: "POLE mutation", etiology: "POLE proofreading deficiency", cancerTypes: ["Endometrial", "Colorectal"], characteristic: "C>A at TCT" },
  { id: "SBS11", name: "Temozolomide", etiology: "Alkylating agent treatment", cancerTypes: ["Glioblastoma"], characteristic: "C>T at CpC" },
  { id: "SBS22", name: "Aristolochic acid", etiology: "Aristolochic acid exposure", cancerTypes: ["Urothelial", "HCC"], characteristic: "T>A transversions" }
];

// Drug database for treatment recommendations
export const drugDatabase = {
  Osimertinib: { target: "EGFR", class: "TKI", indication: "EGFR-mutant NSCLC", approval: "FDA Approved" },
  Sotorasib: { target: "KRAS G12C", class: "Covalent inhibitor", indication: "KRAS G12C NSCLC", approval: "FDA Approved" },
  Adagrasib: { target: "KRAS G12C", class: "Covalent inhibitor", indication: "KRAS G12C NSCLC", approval: "FDA Approved" },
  Vemurafenib: { target: "BRAF V600", class: "BRAF inhibitor", indication: "BRAF V600E melanoma", approval: "FDA Approved" },
  Dabrafenib: { target: "BRAF V600", class: "BRAF inhibitor", indication: "BRAF V600 melanoma/NSCLC", approval: "FDA Approved" },
  Trametinib: { target: "MEK1/2", class: "MEK inhibitor", indication: "BRAF-mutant cancers", approval: "FDA Approved" },
  Alpelisib: { target: "PIK3CA", class: "PI3K inhibitor", indication: "PIK3CA-mutant breast cancer", approval: "FDA Approved" },
  Olaparib: { target: "PARP", class: "PARP inhibitor", indication: "BRCA-mutant cancers", approval: "FDA Approved" },
  Talazoparib: { target: "PARP", class: "PARP inhibitor", indication: "BRCA-mutant breast cancer", approval: "FDA Approved" },
  Rucaparib: { target: "PARP", class: "PARP inhibitor", indication: "BRCA-mutant ovarian/prostate", approval: "FDA Approved" },
  Pembrolizumab: { target: "PD-1", class: "Checkpoint inhibitor", indication: "MSI-H/dMMR tumors", approval: "FDA Approved" },
  Ivosidenib: { target: "IDH1", class: "IDH1 inhibitor", indication: "IDH1-mutant AML/cholangiocarcinoma", approval: "FDA Approved" },
  Enasidenib: { target: "IDH2", class: "IDH2 inhibitor", indication: "IDH2-mutant AML", approval: "FDA Approved" },
  Gilteritinib: { target: "FLT3", class: "FLT3 inhibitor", indication: "FLT3-mutant AML", approval: "FDA Approved" },
  Midostaurin: { target: "FLT3", class: "Multikinase inhibitor", indication: "FLT3-mutant AML", approval: "FDA Approved" },
  Imatinib: { target: "BCR-ABL/KIT", class: "TKI", indication: "CML, GIST", approval: "FDA Approved" },
  Avapritinib: { target: "KIT/PDGFRA", class: "TKI", indication: "GIST with D842V", approval: "FDA Approved" },
  Selpercatinib: { target: "RET", class: "RET inhibitor", indication: "RET-fusion/mutant cancers", approval: "FDA Approved" },
  Pralsetinib: { target: "RET", class: "RET inhibitor", indication: "RET-fusion cancers", approval: "FDA Approved" },
  Crizotinib: { target: "ALK/ROS1/MET", class: "TKI", indication: "ALK/ROS1-positive NSCLC", approval: "FDA Approved" },
  Alectinib: { target: "ALK", class: "ALK inhibitor", indication: "ALK-positive NSCLC", approval: "FDA Approved" },
  Lorlatinib: { target: "ALK/ROS1", class: "TKI", indication: "ALK-positive NSCLC", approval: "FDA Approved" },
  Entrectinib: { target: "NTRK/ROS1", class: "TKI", indication: "NTRK/ROS1-fusion cancers", approval: "FDA Approved" },
  Erdafitinib: { target: "FGFR", class: "FGFR inhibitor", indication: "FGFR-altered bladder cancer", approval: "FDA Approved" },
  Pemigatinib: { target: "FGFR2", class: "FGFR inhibitor", indication: "FGFR2-fusion cholangiocarcinoma", approval: "FDA Approved" },
  Capmatinib: { target: "MET", class: "MET inhibitor", indication: "MET exon 14 NSCLC", approval: "FDA Approved" },
  Tepotinib: { target: "MET", class: "MET inhibitor", indication: "MET exon 14 NSCLC", approval: "FDA Approved" },
  Trastuzumab: { target: "HER2", class: "Monoclonal antibody", indication: "HER2-positive breast/gastric", approval: "FDA Approved" },
  Neratinib: { target: "HER2", class: "TKI", indication: "HER2-positive breast cancer", approval: "FDA Approved" },
  Ruxolitinib: { target: "JAK1/2", class: "JAK inhibitor", indication: "Myelofibrosis, PV", approval: "FDA Approved" },
  Tazemetostat: { target: "EZH2", class: "EZH2 inhibitor", indication: "EZH2-mutant lymphoma", approval: "FDA Approved" },
  Belzutifan: { target: "HIF-2α", class: "HIF-2α inhibitor", indication: "VHL disease", approval: "FDA Approved" },
  Everolimus: { target: "mTOR", class: "mTOR inhibitor", indication: "TSC, RCC", approval: "FDA Approved" },
  Selumetinib: { target: "MEK", class: "MEK inhibitor", indication: "NF1 plexiform neurofibromas", approval: "FDA Approved" },
  Elacestrant: { target: "ER", class: "SERD", indication: "ESR1-mutant breast cancer", approval: "FDA Approved" }
};

// Statistics
export const databaseStats = {
  totalMutations: allMutations.length,
  totalGenes: genes.length,
  totalCancerTypes: cancerTypes.length,
  mutationsByTier: {
    tierI: allMutations.filter(m => m.tier === "Tier I").length,
    tierII: allMutations.filter(m => m.tier === "Tier II").length,
    tierIII: allMutations.filter(m => m.tier === "Tier III").length
  },
  mutationsByType: {
    missense: allMutations.filter(m => m.consequence === "missense_variant").length,
    nonsense: allMutations.filter(m => m.consequence === "stop_gained").length,
    frameshift: allMutations.filter(m => m.consequence === "frameshift_variant").length,
    fusion: allMutations.filter(m => m.consequence === "gene_fusion").length,
    cnv: allMutations.filter(m => m.consequence?.includes("copy_number")).length,
    splice: allMutations.filter(m => m.consequence?.includes("splice")).length,
    inframe: allMutations.filter(m => m.consequence?.includes("inframe")).length
  },
  mutationsByRole: {
    oncogene: allMutations.filter(m => m.role === "Oncogene").length,
    tsg: allMutations.filter(m => m.role === "TSG").length,
    other: allMutations.filter(m => m.role === "Other").length
  },
  mutationsByOncoKB: {
    level1: allMutations.filter(m => m.oncoKBLevel === "Level 1").length,
    level2: allMutations.filter(m => m.oncoKBLevel === "Level 2").length,
    level3: allMutations.filter(m => m.oncoKBLevel === "Level 3A" || m.oncoKBLevel === "Level 3B").length,
    level4: allMutations.filter(m => m.oncoKBLevel === "Level 4").length
  },
  hotspotMutations: allMutations.filter(m => m.isHotspot).length,
  germlineMutations: allMutations.filter(m => m.somaticGermline === "Germline" || m.somaticGermline === "Both").length,
  actionableMutations: allMutations.filter(m => m.isActionable).length
};

export default {
  allMutations,
  genes,
  cancerTypes,
  chromosomes,
  domains,
  roleOptions,
  oncoKBLevels,
  mutationTypes,
  driverStatuses,
  recurrenceOptions,
  geneInfo,
  pathways,
  cooccurrencePatterns,
  mutationSignatures,
  drugDatabase,
  databaseStats
};
